import * as productOrderApi from '@apis/product-orders';
import * as productApi from '@apis/product';
import * as salesChannelApi from '@apis/sales-channels';
import { RefundProductOrdersResponse } from '@apis/product-orders';
import { TextInput } from '@components/common/TextInput';
import UsedCouponsModal from '@components/common/UsedCouponsModal';
import ChangeScheduleModal from '@components/payment-statement/ChangeScheduleModal';
import ProductOrderDetailModal from '@components/payment-statement/ProductOrderDetailModal';
import RefundModeModal from '@components/payment-statement/RefundModeModal';
import UpdateCompletionDateModal from '@components/payment-statement/UpdateCompletionDateModal';
import { SelectParent } from '@components/product/SelectParent';
import SelectProductModal from '@components/product/SelectProductModal';
import { SelectUser } from '@components/product/SelectUser';
import { useAuthentication } from '@hooks/authentication';
import { usePayment } from '@hooks/payment';
import { ClassInvoiceStatus, LegacyClassInvoiceStatus } from '@models/invoice';
import {
  PaymentStatementReportStatus,
  PaymentStatementSettlementStatus,
  RefundStatus,
  RefundSubmissionStatus,
  UpdatePaymentStatementEventType,
} from '@models/paymentStatement';
import { ProductOrderMemo, ProductOrderViewModel } from '@models/productOrderView';
import { SalesChannelOrder, SalesChannelsType, SALES_CHANNELS_LABEL } from '@models/sales-channels';
import useAddAccountTransferModalStore from '@stores/addAccountTransferModalStore';
import useChangeScheduleModalStore from '@stores/changeScheduleModalStore';
import { useClassInvoiceModalStore } from '@stores/classInvoiceModalStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { usePaymentStatementModalStore } from '@stores/paymentStatementModalStore';
import { useProductOrdersStore } from '@stores/productOrdersStore';
import useRefundModeModalStore from '@stores/refundModeModalStore';
import useUpdateCompletionDateModalStore from '@stores/updateCompletionDateModalStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPhoneNumber } from '@utils/dataTransform';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { cdnImageUrl } from '@utils/image';
import { getPaymentMethodText, getRemainPurchases, combinedRefundItemsByName } from '@utils/payment';
import { formatMembershipDiscountLabel } from '@utils/membership-discount';
import {
  MARKET_SECTION_FILTER_OPTIONS,
  MarketSection,
  getMarketSectionLabel,
  resolveMarketSection,
} from '@utils/market-section';
import { WEB_URL } from '@variables';
import { Button, Checkbox, DatePicker, DatePickerProps, Input, Modal, Popover, Select, Table, Tag } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LinesEllipsis from 'react-lines-ellipsis';
import { PhotoView } from 'react-photo-view';
import { toast } from 'react-toastify';

const REFUND_STATUSES: { [key: string]: any } = {
  all: (
    <div className="badge" style={{ backgroundColor: '#bd334a' }}>
      전체 환불
    </div>
  ),
  partial: (
    <div className="badge" style={{ backgroundColor: '#d944db' }}>
      부분 환불
    </div>
  ),
  requested: (
    <div className="badge" style={{ backgroundColor: '#db9737' }}>
      환불 요청
    </div>
  ),
  accepted: (
    <div className="badge" style={{ backgroundColor: '#2782b3' }}>
      환불 예정
    </div>
  ),
  denied: (
    <div className="badge" style={{ backgroundColor: '#6e6e6e' }}>
      환불 반려
    </div>
  ),
  reserved: (
    <div className="badge" style={{ backgroundColor: 'green' }}>
      환불 예약
    </div>
  ),
  request_cancelled: (
    <div className="badge" style={{ backgroundColor: 'tomato' }}>
      환불 철회
    </div>
  ),
  submission: (
    <div className="badge" style={{ backgroundColor: 'pink' }}>
      환불 접수
    </div>
  ),
  none: '-',
};

interface ScheduleHistory {
  createdAt: string;
  modifier: Modifier;
  beforeItems: BeforeItem[];
  afterItems: any[]; // BeforeItem || string
}

export interface Modifier {
  id: string;
  name: string;
  nickname: string;
  requestedBy: string;
}

export interface BeforeItem {
  name: string;
  fee: number;
  count: number;
}

export interface EditMemoProps {
  data: ProductOrderViewModel;
  onChange: (memo: ProductOrderMemo) => void;
}

export const EditMemo: React.FC<EditMemoProps> = (props) => {
  const memo = props?.data?.memo;
  const [editMemo, setEditMemo] = useState<ProductOrderMemo>();
  const productId = props?.data?.product?.id;
  const isEzwel = props?.data?.channel === SalesChannelsType.EZWEL;

  useEffect(() => {
    if (editMemo) {
      props.onChange && props.onChange(editMemo);
    }
  }, [editMemo]);

  useEffect(() => {
    if (!isEzwel || !productId || String(memo?.payment || '').trim()) {
      return;
    }
    let cancelled = false;
    productApi.getProduct(String(productId)).then((response) => {
      if (cancelled) return;
      const questionOptions = response?.data?.questionOptions || [];
      const activeOptions = questionOptions.filter(
        (option: { questionType?: string }) => option?.questionType !== 'preferredSchedule',
      );
      const labels = activeOptions.length
        ? activeOptions
        : [{ question: '참가자 이름' }, { question: '학년' }, { question: '함께 신청한 친구(없을 경우 X)' }];
      const template = labels
        .map((option: { question?: string }) => `${String(option?.question || '').trim()}:`)
        .filter((part: string) => part !== ':')
        .join(' / ');
      if (!template) return;
      setEditMemo((prev) => {
        if (String(prev?.payment || memo?.payment || '').trim()) {
          return prev;
        }
        return { ...prev, payment: template } as ProductOrderMemo;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isEzwel, productId, memo?.payment]);

  return (
    <div>
      <div style={{ height: '44px', fontSize: '16px', fontWeight: 'bold' }}>고객 메모 수정</div>
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontWeight: 'bold' }}>결제 메모</div>
        <Input.TextArea
          value={editMemo?.payment ?? memo?.payment ?? ''}
          onChange={(e) => {
            setEditMemo((prev) => {
              return { ...prev, payment: e.target.value } as ProductOrderMemo;
            });
          }}></Input.TextArea>
      </div>
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontWeight: 'bold' }}>
          {props?.data?.channel === SalesChannelsType.NAVER_STORE ? '고객 요청사항' : '배송 메모'}
        </div>
        <Input.TextArea
          value={editMemo?.shipping ?? memo?.shipping ?? ''}
          onChange={(e) => {
            setEditMemo((prev) => {
              return { ...prev, shipping: e.target.value } as ProductOrderMemo;
            });
          }}></Input.TextArea>
      </div>
    </div>
  );
};

export interface EditBuyerInfoProps {
  data: ProductOrderViewModel;
  onChange: (buyerInfo: {
    buyer: ProductOrderViewModel['buyer'];
    receiver?: ProductOrderViewModel['receiver'];
  }) => void;
}

export const EditBuyerInfo: React.FC<EditBuyerInfoProps> = (props) => {
  const buyer = props.data?.buyer;
  const receiver = props.data?.receiver;
  const [editBuyerInfo, setEditBuyerInfo] = useState<{
    buyer: ProductOrderViewModel['buyer'];
    receiver: ProductOrderViewModel['receiver'];
  }>({} as any);
  useEffect(() => {
    if (editBuyerInfo) {
      props.onChange && props.onChange(editBuyerInfo);
    }
  }, [editBuyerInfo]);

  return (
    <div>
      <div style={{ height: '44px', fontSize: '16px', fontWeight: 'bold' }}>구매자 정보 수정</div>
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontWeight: 'bold' }}>구매자</div>
        이름:
        <Input
          value={editBuyerInfo?.buyer?.name ?? buyer?.name ?? ''}
          onChange={(e) => {
            setEditBuyerInfo((prev) => {
              return { ...prev, buyer: { ...prev?.buyer, name: e.target.value } };
            });
          }}></Input>
        연락처:
        <Input
          value={editBuyerInfo?.buyer?.phone ?? buyer?.phone ?? ''}
          onChange={(e) => {
            setEditBuyerInfo((prev) => {
              return { ...prev, buyer: { ...prev?.buyer, phone: e.target.value } };
            });
          }}></Input>
      </div>
      {props.data?.channel && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontWeight: 'bold' }}>수취인</div>
          이름:
          <Input
            value={editBuyerInfo?.receiver?.name ?? receiver?.name ?? ''}
            onChange={(e) => {
              setEditBuyerInfo((prev) => {
                return { ...prev, receiver: { ...prev?.receiver, name: e.target.value } };
              });
            }}></Input>
          연락처:
          <Input
            value={editBuyerInfo?.receiver?.phone ?? receiver?.phone ?? ''}
            onChange={(e) => {
              setEditBuyerInfo((prev) => {
                return { ...prev, receiver: { ...prev?.receiver, phone: e.target.value } };
              });
            }}></Input>
        </div>
      )}
    </div>
  );
};

export interface CreateBankAccountTransferOrderProps {
  onClose: () => void;
  onCreate: () => void;
}

export const CreateBankAccountTransferOrder: React.FC<CreateBankAccountTransferOrderProps> = (props) => {
  const { onClose, onCreate } = props;
  const [newOrder, setNewOrder] = useState<Partial<SalesChannelOrder>>({});
  const [manualProductInput, setManualProductInput] = useState<boolean>(false);
  const [manualBuyerInput, setManualBuyerInput] = useState<boolean>(false);
  const [showProductSelect, setShowProductSelect] = useState<boolean>(false);
  const dialog = useOkCancelDialog();

  const createOrderMutation = useMutation({
    mutationFn: salesChannelApi.createBankAccountTransferOrder,
    onSuccess: async (response) => {
      if (response.status === 201) {
        setNewOrder({});
        onCreate && onCreate();
      }
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: response.status === 201 ? '완료' : '실패',
        content: response.status === 201 ? `결제내역 생성되었습니다.` : '에러가 발생했습니다.',
      });
    },
  });

  const onCreateBankAccountTransferOrder = () => {
    const newSalesOrder: Partial<SalesChannelOrder> = {
      channel: newOrder?.channel,
      seller: newOrder?.seller,
      buyer: newOrder?.buyer,
      product: newOrder?.product,
      purchase: newOrder?.purchase,
      payment: {
        method: 'bank_account_transfer',
        paidAt: new Date(),
        amount: newOrder?.purchase?.fee || 0,
        billingAmount: 0,
        bankAccountTransferAmount: newOrder?.purchase?.fee || 0,
      },
      memo: newOrder?.memo,
      statuses: {
        payment: 'done',
        refund: 'none',
        settlement: 'none',
      },
    };
    createOrderMutation.mutate(newSalesOrder);
  };

  return (
    <div>
      <div style={{ fontWeight: 'bold', fontSize: '16px' }}>결제내역 생성(계좌이체)</div>
      <div
        style={{
          padding: '12px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'flex-start',
        }}>
        <div>
          <div style={{ fontWeight: 'bold' }}>결제 플랫폼 선택</div>
          <Select
            placeholder="선택"
            value={newOrder?.channel}
            style={{ width: 120 }}
            onChange={(value) => {
              setNewOrder((prev) => {
                return { ...prev, channel: value };
              });
            }}
            options={[
              { value: 'inbound-app', label: '앱' },
              { value: 'inbound-naver', label: '네이버' },
            ]}
          />
        </div>
        <div>
          <div style={{ fontWeight: 'bold' }}>판매방식 선택</div>
          <Select
            placeholder="선택"
            value={newOrder?.product?.saleType}
            style={{ width: 120 }}
            onChange={(value) => {
              setNewOrder((prev) => {
                const newProduct = {
                  ...prev.product,
                  saleType: value,
                  name: prev?.product?.name || '',
                };
                return { ...prev, product: newProduct };
              });
            }}
            options={[
              { value: 'commerce', label: '커머스' },
              { value: 'class', label: '클래스' },
            ]}
          />
        </div>
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold' }}>파트너 선택</div>
          <SelectUser
            onSelect={(partner) => {
              setNewOrder((prev) => {
                const seller = {
                  id: partner?._id,
                  name: partner?.profile?.nickname,
                  phone: partner?.profile?.phone,
                };
                const newValue = { ...prev, seller };
                delete newValue['product'];
                return newValue;
              });
            }}></SelectUser>
        </div>
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold' }}>
            구매자 선택
            <Checkbox
              style={{ marginLeft: '10px' }}
              checked={manualBuyerInput}
              onChange={(e) => {
                setManualBuyerInput(e.target.checked);
              }}>
              직접입력
            </Checkbox>
          </div>
          {!manualBuyerInput ? (
            <SelectParent
              onSelect={(parent) => {
                setNewOrder((prev) => {
                  const buyer = {
                    id: parent?._id,
                    name: parent?.profile?.name || '',
                    phone: parent?.profile?.phone || '',
                  };
                  return { ...prev, buyer };
                });
              }}></SelectParent>
          ) : (
            <div>
              <div>
                이름:
                <TextInput
                  onChangeValue={(value) => {
                    setNewOrder((prev) => {
                      const newBuyer = {
                        ...prev.buyer,
                        name: value,
                        phone: prev?.buyer?.phone || '',
                      };
                      return { ...prev, buyer: newBuyer };
                    });
                  }}></TextInput>
              </div>
              <div>
                연락처:
                <TextInput
                  onChangeValue={(value) => {
                    setNewOrder((prev) => {
                      const newBuyer = {
                        ...prev.buyer,
                        phone: value,
                        name: prev?.buyer?.name || '',
                      };
                      return { ...prev, buyer: newBuyer };
                    });
                  }}></TextInput>
              </div>
            </div>
          )}
        </div>
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold' }}>
            상품 선택
            <Checkbox
              style={{ marginLeft: '10px' }}
              checked={manualProductInput}
              onChange={(e) => {
                setManualProductInput(e.target.checked);
              }}>
              직접입력
            </Checkbox>
          </div>
          {!manualProductInput ? (
            newOrder?.product?.name ? (
              <div>
                <div>선택된 상품: {newOrder?.product?.name}</div>
                <Button
                  disabled={!!newOrder?.seller?.id}
                  onClick={() =>
                    setNewOrder((prev) => {
                      const newValue = { ...prev };
                      delete newValue['product'];
                      return newValue;
                    })
                  }>
                  삭제
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => {
                  if (newOrder?.seller?.name) {
                    setShowProductSelect(true);
                  }
                }}>
                선택
              </Button>
            )
          ) : (
            <div>
              상품 이름:
              <TextInput
                onChangeValue={(value) => {
                  setNewOrder((prev) => {
                    return { ...prev, product: { ...prev.product, name: value } };
                  });
                }}></TextInput>
            </div>
          )}
          {newOrder?.seller?.name && showProductSelect && (
            <SelectProductModal
              selectLimit={1}
              sellerId={newOrder?.seller?.id}
              open={showProductSelect}
              onAddProducts={(products) => {
                setNewOrder((prev) => {
                  return {
                    ...prev,
                    product: { id: products[0]._id, name: products[0].title, saleType: products[0].saleType },
                  };
                });
              }}
              onClose={() => {
                setShowProductSelect(false);
              }}></SelectProductModal>
          )}
          <div style={{ width: '100%', marginTop: '12px' }}>
            <div style={{ fontWeight: 'bold' }}>
              상품옵션 입력(수업일 포함) 예시: ( 삼국시대 / 11/30(토) / 15:30 )
              <div style={{ color: 'red', fontSize: '13px' }}>
                * 수업일은 하루만 입력 가능합니다. 여러날이 입력될 경우 마지막 날짜만 유효합니다.
              </div>
            </div>
            <TextInput
              placeholder="ex) 삼국시대 / 11/30(토) / 15:30"
              onChangeValue={(value) => {
                setNewOrder((prev) => {
                  const newPurchase = {
                    ...prev?.purchase,
                    name: `${value}`,
                    fee: prev?.purchase?.fee || 0,
                    count: 1,
                  };
                  return { ...prev, purchase: newPurchase };
                });
              }}></TextInput>
          </div>
          <div style={{ width: '100%' }}>
            <div style={{ fontWeight: 'bold' }}>금액 입력</div>
            <TextInput
              type="number"
              placeholder="숫자만 입력해주세요."
              onChangeValue={(value) => {
                setNewOrder((prev) => {
                  const newPurchase = {
                    ...prev?.purchase,
                    name: prev?.purchase?.name || '',
                    fee: Number(value),
                    count: 1,
                  };
                  return { ...prev, purchase: newPurchase };
                });
              }}></TextInput>
          </div>
          <div style={{ width: '100%' }}>
            <div style={{ fontWeight: 'bold' }}>메모</div>
            <TextInput
              onChangeValue={(value) => {
                setNewOrder((prev) => {
                  return { ...prev, memo: { ...prev?.memo, payment: `${value}` } };
                });
              }}></TextInput>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '10px' }}>
        <Button
          onClick={() => {
            onCreateBankAccountTransferOrder();
          }}>
          생성
        </Button>
        <Button
          onClick={() => {
            onClose && onClose();
          }}>
          취소
        </Button>
      </div>
    </div>
  );
};

export interface UpdateNaverRefundModalProps {
  data: ProductOrderViewModel;
  onClose: () => void;
  onUpdate: () => void;
}

export const UpdateNaverRefundModal: React.FC<UpdateNaverRefundModalProps> = (props) => {
  const { data, onClose, onUpdate } = props;
  const dialog = useOkCancelDialog();
  const [refundStatus, setRefundStatus] = useState<RefundStatus>(data?.statuses?.refund || RefundStatus.NONE);
  const [refundAmount, setRefundAmount] = useState<number>(data?.refund?.amount || 0);
  const [refundMemo, setRefundMemo] = useState<string>(data?.memo?.refund || '');

  const updateSalesChannelOrderRefundDataMutation = useMutation({
    mutationFn: salesChannelApi.updateSalesChannelOrderRefundData,
    onSuccess: async () => {
      onUpdate();
      toast.dark('수정 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: `${e?.response?.data?.message}`,
      });
    },
  });

  const handleUpdate = () => {
    const paymentAmount = data?.payment?.amount || 0;
    if (refundAmount > paymentAmount) {
      dialog.open({
        title: '오류',
        content: `환불 금액이 결제 금액을 초과합니다.\n결제 금액: ${paymentAmount.toLocaleString()}원\n입력한 환불액: ${refundAmount.toLocaleString()}원`,
        type: 'ok',
      });
      return;
    }

    const updateData = {
      salesChannelOrderId: data._id,
      refundStatus,
      refundAmount,
      refundMemo,
    };
    updateSalesChannelOrderRefundDataMutation.mutate(updateData);
  };

  const paymentAmount = data?.payment?.amount || 0;
  const isRefundExceeded = refundAmount > paymentAmount;

  return (
    <Modal
      title="외부채널 환불 정보 업데이트"
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button
          key="update"
          type="primary"
          onClick={handleUpdate}
          loading={updateSalesChannelOrderRefundDataMutation.isPending}>
          수정
        </Button>,
      ]}
      width={500}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>주문 정보</div>
          <div>채널: {SALES_CHANNELS_LABEL[data.channel]}</div>
          <div>주문번호: {data.uid}</div>
          <div>상품명: {data.product?.name}</div>
          <div>구매자: {data.buyer?.name}</div>
          <div>결제금액: {data.payment?.amount?.toLocaleString()}원</div>
          <div>누적환불액: {(data.refund?.amount || 0).toLocaleString()}원</div>
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>환불 상태</div>
          <Select
            value={refundStatus}
            onChange={setRefundStatus}
            style={{ width: '100%' }}
            options={[
              { value: RefundStatus.PARTIAL, label: '부분환불' },
              { value: RefundStatus.ALL, label: '전체환불' },
              { value: RefundStatus.NONE, label: '환불안함' },
            ]}
          />
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>전체 환불 금액</div>
          <Input
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(Number(e.target.value))}
            placeholder="전체 환불 금액을 입력하세요"
            suffix="원"
          />
          {isRefundExceeded && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
              ⚠️ 환불 금액이 결제 금액을 초과합니다. (입력한 환불액: {refundAmount.toLocaleString()}원)
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            입력한 금액이 전체 환불액으로 설정됩니다.
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>환불 메모</div>
          <Input.TextArea
            value={refundMemo}
            onChange={(e) => setRefundMemo(e.target.value)}
            placeholder="환불 관련 메모를 입력하세요"
            rows={4}
          />
        </div>
      </div>
    </Modal>
  );
};

const ezwelPartialRefundEstimatedAmount = (paymentAmount: number, refundAmount: number, ratePercent?: number) => {
  const rate = ratePercent ?? 15;
  const remainAmount = paymentAmount - refundAmount;
  return Math.max(0, Math.floor(remainAmount * (1 - rate / 100)));
};

export const EzwelPartialRefundModal: React.FC<{
  data: ProductOrderViewModel;
  onClose: () => void;
  onUpdate: () => void;
}> = (props) => {
  const { data, onClose, onUpdate } = props;
  const dialog = useOkCancelDialog();
  const paymentAmount = data?.payment?.amount || 0;
  const ratePercent = data.commissionRate?.ratePercent;
  const [refundRatio, setRefundRatio] = useState<number>(50);
  const [refundMemo, setRefundMemo] = useState<string>(data?.memo?.refund || '이지웰 부분환불 정산 반영');
  const refundAmount = Math.floor((paymentAmount * refundRatio) / 100);

  const applyMutation = useMutation({
    mutationFn: salesChannelApi.applyEzwelPartialRefund,
    onSuccess: async () => {
      onUpdate();
      toast.dark('부분환불을 반영하고 이지웰 사용처리를 요청했습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    },
    onError: (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message || '부분환불 설정에 실패했습니다.',
        type: 'ok',
      });
    },
  });

  const settlementTarget = Math.max(0, paymentAmount - refundAmount);
  const estimatedAmount = ezwelPartialRefundEstimatedAmount(paymentAmount, refundAmount, ratePercent);
  const isInvalidAmount =
    !Number.isFinite(refundRatio) ||
    refundRatio <= 0 ||
    refundRatio >= 100 ||
    refundAmount <= 0 ||
    refundAmount >= paymentAmount;

  const handleApply = () => {
    if (isInvalidAmount) {
      dialog.open({
        title: '오류',
        content: '환불 비율은 0%보다 크고 100%보다 작아야 합니다.',
        type: 'ok',
      });
      return;
    }
    dialog.open({
      title: '부분환불 설정',
      content: (
        <div>
          환불 {refundRatio}%({refundAmount.toLocaleString()}원), 정산예정 {estimatedAmount.toLocaleString()}
          원으로 반영합니다.
          <br />
          이지웰에는 이 쿠폰을 사용완료로 통보합니다. 주문에 다른 미사용 쿠폰이 없으면 주문 이용완료일도 등록됩니다.
        </div>
      ),
      type: 'ok_cancel',
      onConfirm: () => {
        applyMutation.mutate({
          salesChannelOrderId: data._id,
          refundAmount,
          refundMemo,
        });
      },
    });
  };

  return (
    <Modal
      title="이지웰 부분환불 설정"
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          loading={applyMutation.isPending}
          disabled={isInvalidAmount}>
          반영
        </Button>,
      ]}
      width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <div>주문번호: {data.uid}</div>
          <div>구매자: {data.buyer?.name}</div>
          <div>결제금액: {paymentAmount.toLocaleString()}원</div>
          <div>수수료율: {ratePercent ?? 15}%</div>
        </div>
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>환불 비율</div>
          <Input
            type="number"
            min={1}
            max={99}
            value={refundRatio}
            onChange={(e) => setRefundRatio(Number(e.target.value))}
            suffix="%"
          />
          {isInvalidAmount && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
              환불 비율은 0%보다 크고 100%보다 작아야 합니다.
            </div>
          )}
        </div>
        <div>
          <div>환불금액: {refundAmount.toLocaleString()}원</div>
          <div>정산대상: {settlementTarget.toLocaleString()}원</div>
          <div>정산예정: {estimatedAmount.toLocaleString()}원</div>
        </div>
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>환불 메모</div>
          <Input.TextArea value={refundMemo} onChange={(e) => setRefundMemo(e.target.value)} rows={3} />
        </div>
      </div>
    </Modal>
  );
};

export const ProductOrdersPage: React.FC = () => {
  useAuthentication();
  const store = useProductOrdersStore();
  const payment = usePayment();
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const classInvoiceModalStore = useClassInvoiceModalStore();
  const paymentStatementModalStore = usePaymentStatementModalStore();
  const changeScheduleModalStore = useChangeScheduleModalStore();
  const refundModeModalStore = useRefundModeModalStore();
  const addAccountTransferModalStore = useAddAccountTransferModalStore();
  const updateCompletionDateModalStore = useUpdateCompletionDateModalStore();
  const searchInputRef = useRef<any>();
  const [searchParams] = useSearchParams();
  const keywordFromUrl = searchParams.get('keyword')?.trim() || '';
  const appliedKeywordRef = useRef('');
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(keywordFromUrl);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number[]>([]);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [selectedScheduleHistory, setSelectedScheduleHistory] = useState<ScheduleHistory[]>([]);
  const [editItem, setEditItem] = useState<{ data: ProductOrderViewModel; type: 'memo' | 'buyerInfo' }>();
  const [editMemo, setEditMemo] = useState<ProductOrderMemo>();
  const [editBuyerInfo, setEditBuyerInfo] = useState<{
    buyer: ProductOrderViewModel['buyer'];
    receiver?: ProductOrderViewModel['receiver'];
  }>();
  const [showCreateOrder, setShowCreateOrder] = useState<boolean>(false);
  const memoRef = useRef<any>();
  const [showUpdateNaverRefundModal, setShowUpdateNaverRefundModal] = useState<boolean>(false);
  const [selectedNaverOrder, setSelectedNaverOrder] = useState<ProductOrderViewModel | null>(null);
  const [ezwelPartialRefundOrder, setEzwelPartialRefundOrder] = useState<ProductOrderViewModel | null>(null);
  const [showUsedCouponsModal, setShowUsedCouponsModal] = useState<boolean>(false);
  const [selectedUsedCoupons, setSelectedUsedCoupons] = useState<any[]>([]);
  const [isDownloadingBasic, setIsDownloadingBasic] = useState<boolean>(false);
  const [isDownloadingPB, setIsDownloadingPB] = useState<boolean>(false);
  const [isDownloadingCommerce, setIsDownloadingCommerce] = useState<boolean>(false);
  const [isDownloadingMarketing, setIsDownloadingMarketing] = useState<boolean>(false);
  const [detailOrder, setDetailOrder] = useState<ProductOrderViewModel | null>(null);

  useEffect(() => {
    if (!keywordFromUrl) {
      return;
    }
    const applyKeyword = () => {
      if (appliedKeywordRef.current === keywordFromUrl) {
        return;
      }
      appliedKeywordRef.current = keywordFromUrl;
      setSearchKeyword(keywordFromUrl);
      useProductOrdersStore.getState().updateSearchQuery({
        keyword: keywordFromUrl,
        page: 0,
        startDate: undefined,
        endDate: undefined,
        channel: 'all',
        saleType: 'all',
        marketSection: 'all',
        refundStatus: undefined,
        settlementStatus: undefined,
        completionStatus: undefined,
        type: 'all',
        isSettlementTarget: false,
        isPartnerRefundRequested: false,
      });
    };
    if (useProductOrdersStore.persist.hasHydrated()) {
      applyKeyword();
      return;
    }
    return useProductOrdersStore.persist.onFinishHydration(applyKeyword);
  }, [keywordFromUrl]);

  const productOrderSearchQuery = useQuery({
    queryKey: [
      `admin-search-product-orders`,
      JSON.stringify(store.searchQuery),
      store.searchKeyword,
      paymentStatementModalStore.show,
      dialog.show,
    ],
    queryFn: () => productOrderApi.searchProductOrders(store.searchQuery),
  });

  const cancelEzwelPartialRefundMutation = useMutation({
    mutationFn: (salesChannelOrderId: string) => salesChannelApi.cancelEzwelPartialRefund(salesChannelOrderId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      toast.dark('부분환불을 취소하고 이전 상태로 되돌렸습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    },
    onError: (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message || '부분환불 취소에 실패했습니다.',
        type: 'ok',
      });
    },
  });

  const markEzwelRefundScheduledMutation = useMutation({
    mutationFn: (salesChannelOrderId: string) => salesChannelApi.markEzwelRefundScheduled(salesChannelOrderId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      toast.dark('환불예정으로 변경했습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    },
    onError: (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message || '환불예정 변경에 실패했습니다.',
        type: 'ok',
      });
    },
  });

  const updateProductOrderSchedulesMutation = useMutation({
    mutationFn: ({ uid, channel }: { uid: string; channel: string }) =>
      productOrderApi.updateProductOrderSchedules(uid, channel),
    onSuccess: async (response) => {
      if (response.status >= 200 && response.status < 300) {
        toast.success('수업일이 성공적으로 업데이트되었습니다.');
        // 쿼리 무효화로 데이터 새로고침
        await queryClient.invalidateQueries({ queryKey: [`admin-search-product-orders`] });
      } else {
        toast.error('수업일 업데이트에 실패했습니다.');
      }
    },
    onError: (error) => {
      console.error('Error updating schedules:', error);
      toast.error('수업일 업데이트 중 오류가 발생했습니다.');
    },
  });

  useEffect(() => {
    const targets: number[] = [];
    if (checkAll) {
      if (store.searchResultContainer.contents && store.searchResultContainer.contents.length > 0) {
        const { page = 0, size = 0 } = store.searchQuery;
        const maxSize = Math.min((page + 1) * size, store.searchResultContainer.contents.length);

        const currentPageResultList = store.searchResultContainer.contents.slice(page * size, maxSize) || [];
        currentPageResultList.map((productOrder, idx) => {
          const saleType = productOrder?.product?.saleType || 'commerce';
          const commerceTypeCondition =
            saleType === 'commerce' &&
            productOrder?.channel !== SalesChannelsType.EZWEL &&
            [RefundStatus.RESERVED, RefundStatus.ACCEPTED, RefundStatus.NONE].includes(
              productOrder.statuses.refund || RefundStatus.NONE,
            );
          const classTypeCondition = saleType === 'class' && store.searchQuery.saleType === 'class';
          if (commerceTypeCondition || classTypeCondition) {
            targets.push(idx);
          }
        });
        setSelectedItemIndex(targets);
      }
    } else {
      setSelectedItemIndex([]);
    }
  }, [checkAll]);

  const TABLE_COLUMNS: ColumnsType<ProductOrderViewModel> = [
    {
      title: () => {
        const isAllType = store.searchQuery.saleType === 'all';
        return (
          <Checkbox
            onChange={() => {
              setCheckAll((value) => {
                return !value;
              });
            }}
            checked={checkAll}
            disabled={isAllType}></Checkbox>
        );
      },
      render: (data: ProductOrderViewModel, _, index) => {
        if (!data) {
          return '-';
        }
        const checked = selectedItemIndex.includes(index);
        return (
          <Checkbox
            disabled={
              data?.channel === SalesChannelsType.EZWEL ||
              (data?.product?.saleType === 'commerce' &&
                ![RefundStatus.RESERVED, RefundStatus.ACCEPTED, RefundStatus.NONE].includes(
                  data?.statuses?.refund || RefundStatus.NONE,
                ))
            }
            checked={checked}
            onChange={() => {
              setSelectedItemIndex((prev) => {
                const newValue = [...prev];
                const isInclude = newValue.includes(index);
                if (isInclude) {
                  const idx = newValue.indexOf(index);
                  newValue.splice(idx, 1);
                } else if (data?._id) {
                  newValue.push(index);
                }
                return newValue;
              });
            }}></Checkbox>
        );
      },
      fixed: 'left',
    },
    {
      title: 'ID',
      dataIndex: '_id',
      render: (_id, record) => {
        if (!record) {
          return '-';
        }
        const isSalesChannelOrder = record?.channel ? true : false;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(_id);
                toast.dark('결제내역 ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID 복사
            </Button>
            {record?.uuid && (
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(record.uuid);
                  toast.dark('토스페이먼츠 UUID를 클립보드에 복사했습니다', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    draggable: true,
                    progress: undefined,
                    bodyStyle: {
                      color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                      textAlign: 'center',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '20px',
                    },
                    theme: 'dark',
                  });
                }}>
                토스 UUID 복사
              </Button>
            )}
            {isSalesChannelOrder ? (
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(record?.uid);
                  toast.dark('상품주문번호를 클립보드에 복사했습니다', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    draggable: true,
                    progress: undefined,
                    bodyStyle: {
                      color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                      textAlign: 'center',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '20px',
                    },
                    theme: 'dark',
                  });
                }}>
                상품주문번호
              </Button>
            ) : (
              <Button
                onClick={() => {
                  window.open(`${WEB_URL}/product/${record?.product.id}`);
                }}>
                상품 바로가기
              </Button>
            )}
          </div>
        );
      },
      fixed: 'left',
    },
    {
      title: '채널',
      width: 100,
      render: (record) => {
        if (!record) {
          return '-';
        }
        if (record?.channel) {
          if ([SalesChannelsType.NAVER_STORE].includes(record?.channel)) {
            return '네이버';
          } else if ([SalesChannelsType.KAKAO_RESERVATION].includes(record?.channel)) {
            return '카카오';
          } else if ([SalesChannelsType.INBOUND_APP].includes(record?.channel)) {
            return '인바운드\n앱';
          } else if ([SalesChannelsType.INBOUND_NAVER].includes(record?.channel)) {
            return '인바운드\n네이버';
          } else if ([SalesChannelsType.BENEPIA].includes(record?.channel)) {
            return `베네피아\n(${record?.buyer?.sitename || '-'})`;
          } else if ([SalesChannelsType.EZWEL].includes(record?.channel)) {
            return '이지웰';
          } else {
            return '-';
          }
        }
        return '앱';
      },
      fixed: 'left',
      align: 'center',
    },
    {
      title: '구분',
      dataIndex: 'product',
      render: (product) => {
        let typeText = '커머스';
        const saleType = product?.saleType;
        if (saleType === 'class') {
          typeText = '클래스';
        }
        return typeText;
      },
      fixed: 'left',
    },
    {
      title: '라스트찬스 구분',
      width: 110,
      fixed: 'left',
      align: 'center',
      render: (record: ProductOrderViewModel) => {
        const section = resolveMarketSection(record?.product);
        if (!section) {
          return '-';
        }
        const color =
          section === MarketSection.LAST_CHANCE ? 'orange' : section === MarketSection.HOT_DEAL ? 'red' : 'volcano';
        return <Tag color={color}>{getMarketSectionLabel(record?.product)}</Tag>;
      },
    },
    {
      title: '환불(상태)',
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }
        const isEzwel = data.channel === SalesChannelsType.EZWEL;
        const settlementDone = `${data.statuses?.settlement || ''}`.toLowerCase() === 'done';
        return (
          <div>
            <div style={{ marginBottom: '3px' }}>{REFUND_STATUSES[data?.statuses?.refund] || '-'}</div>
            {isEzwel ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {data.statuses.refund === RefundStatus.ALL ? (
                  <Button
                    onClick={() => {
                      refundModeModalStore.open(data);
                    }}>
                    내역보기
                  </Button>
                ) : (
                  <>
                    {data.statuses.refund !== RefundStatus.ACCEPTED && (
                      <Button
                        onClick={() => {
                          dialog.open({
                            title: '이지웰 환불 안내',
                            content: (
                              <div>
                                이지웰 결제건은 어드민에서 환불할 수 없습니다.
                                <br />
                                상품주문번호와 상세내용을 이지웰 소통 담당자에게 전달해주세요.
                                <br />이 주문을 환불예정으로 변경할까요?
                              </div>
                            ),
                            type: 'ok_cancel',
                            onConfirm: () => {
                              markEzwelRefundScheduledMutation.mutate(data._id);
                            },
                          });
                        }}>
                        환불예정하기
                      </Button>
                    )}
                    {!settlementDone && (
                      <Button
                        onClick={() => {
                          setEzwelPartialRefundOrder(data);
                        }}>
                        부분환불 설정
                      </Button>
                    )}
                    {!settlementDone && data.statuses.refund === RefundStatus.PARTIAL && (
                      <Button
                        onClick={() => {
                          dialog.open({
                            title: '부분환불 취소',
                            content: (
                              <div>
                                마지막 부분환불 설정을 취소하고 직전 환불 상태와 정산예정액으로 되돌립니다.
                                <br />
                                완료일과 이지웰 사용처리는 유지됩니다.
                              </div>
                            ),
                            type: 'ok_cancel',
                            onConfirm: () => {
                              cancelEzwelPartialRefundMutation.mutate(data._id);
                            },
                          });
                        }}>
                        부분환불 취소
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <Button
                onClick={() => {
                  const newData = { ...data };
                  if (data?.channel === SalesChannelsType.NAVER_STORE) {
                    const purchases =
                      data?.purchases?.length > 0 ? data.purchases : data?.purchase ? [data.purchase] : [];
                    const paidAmount = data?.payment?.amount || 0;
                    newData['purchases'] = purchases.map((item) => {
                      return {
                        ...item,
                        fee: paidAmount / (item?.count || 1),
                      };
                    });
                  }
                  refundModeModalStore.open(newData);
                }}>
                {data.statuses.refund === RefundStatus.ALL ? '내역보기' : '환불하기'}
              </Button>
            )}
          </div>
        );
      },
      fixed: 'left',
      align: 'center',
    },
    {
      title: '환불접수\n(상태)',
      align: 'center',
      width: 90,
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }
        let status = '-';
        if (data?.refund?.submissionStatus === RefundSubmissionStatus.NOTIFIED) {
          status = '환불 접수';
        }

        const isRefundAll = data?.statuses.refund === RefundStatus.ALL;
        if (isRefundAll) {
          return '-';
        }

        return (
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>{status}</div>
            {!data?.channel && (
              <div>
                <Button
                  onClick={() => {
                    if (confirm('발송 하시겠습니까?')) {
                      payment.updateRefundStatus(data._id, UpdatePaymentStatementEventType.SUBMISSION);
                    }
                  }}>
                  발송
                </Button>
                {data.refund?.submissionStatus === RefundSubmissionStatus.NOTIFIED ? (
                  <Button
                    style={{ marginTop: '5px' }}
                    onClick={() => {
                      if (confirm('취소 하시겠습니까?')) {
                        payment.updateRefundStatus(data._id, UpdatePaymentStatementEventType.CANCEL_SUBMISSION);
                      }
                    }}>
                    접수 취소
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        );
      },
      fixed: 'left',
    },
    {
      title: '정산상태',
      align: 'center',
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }
        const settlementStatus = (data.statuses?.settlement || '').toLowerCase();
        const isExcludedTarget = data?.settlement?.excluded;
        let status: any = '-';
        if (settlementStatus === 'done' && data?.settlement?.transferAt) {
          const transperAt = moment(data?.settlement?.transferAt).format('YYYY-MM-DD HH:mm');
          status = `정산완료\n(${transperAt})`;
        }
        if (isExcludedTarget) {
          const transferAt = !!data?.settlement?.transferAt;
          status = (
            <span
              style={{
                backgroundColor: 'red',
                padding: '2px 4px',
                borderRadius: '4px',
                color: 'white',
                fontWeight: 'bold',
              }}>
              정산 제외{transferAt ? `\n(오류확인필요)` : ''}
            </span>
          );
        }
        return (
          <div>
            <div>{status}</div>
            {!isExcludedTarget && (
              <Button
                style={{ marginTop: '4px' }}
                onClick={() => {
                  dialog.open({
                    title: '정산대상 제외',
                    content: '정산대상 제외 하시겠습니까?',
                    type: 'ok_cancel',
                    onConfirm: () => {
                      excludeSettlementTargetMutation.mutate({
                        uid: data?.uid || data?.uuid,
                      });
                    },
                  });
                }}>
                정산대상 제외
              </Button>
            )}
          </div>
        );
      },
      width: 100,
      fixed: 'left',
    },
    {
      title: '구매자(수취인)',
      align: 'center',
      render: (record) => {
        if (!record) {
          return '-';
        }
        const buyer = record?.buyerDetail?.[0] || record?.buyer;
        const buyerId = buyer?.id || buyer?._id;
        const buyerName = buyer?.profile?.name || buyer?.name;
        const buyerPhone = `${buyer?.profile?.phone || buyer?.phone || ''}`.replace(/-/g, '');
        const receiverName = record?.receiver?.name;
        const receiverPhone = `${record?.receiver?.phone || ''}`.replace(/-/g, '');
        const differentName = !!receiverName && receiverName !== buyerName;
        const differentPhone = !!receiverPhone && receiverPhone !== buyerPhone;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {buyer ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>{`${buyerName || '-'}${differentName ? `(${receiverName})` : ''}`}</div>
                <div>{`${formatPhoneNumber(buyer?.profile?.phone || buyer?.phone)}${
                  differentPhone ? `\n(${formatPhoneNumber(record?.receiver?.phone)})` : ''
                }`}</div>
                <div>
                  {buyer?.membership?.name && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        background: 'var(--Blue-10, #EBF9FF)',
                        color: 'var(--Blue-60, #1890FF)',
                        fontFamily: 'Pretendard',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        padding: '4px',
                      }}>
                      {buyer?.membership?.name}
                    </div>
                  )}
                </div>
                {buyerId && (
                  <div style={{ marginBottom: '4px' }}>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(buyerId);
                        toast.dark(`구매자 ID를 클립보드에 복사했습니다(${buyerId})`, {
                          position: 'top-center',
                          autoClose: 3000,
                          hideProgressBar: true,
                          closeOnClick: true,
                          pauseOnHover: true,
                          pauseOnFocusLoss: false,
                          draggable: true,
                          progress: undefined,
                          bodyStyle: {
                            color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                            textAlign: 'center',
                            fontFamily: 'Pretendard',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '20px',
                          },
                          theme: 'dark',
                        });
                      }}>
                      ID 복사
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              '-'
            )}
            <Button
              onClick={() => {
                setEditItem({ data: record, type: 'buyerInfo' });
              }}>
              수정
            </Button>
          </div>
        );
      },
      fixed: 'left',
    },
    {
      title: '판매자',
      dataIndex: 'sellerDetail',
      align: 'center',
      render: (seller) => {
        if (!seller || seller.length === 0) {
          return '-';
        }
        const sellerBankAccountNumber = seller[0]?.profile?.contract?.bankAccountNumber;

        return (
          <div>
            <div>{`${seller[0].profile?.name}${
              seller[0].profile?.nickname ? `\n(${seller[0].profile?.nickname})` : ''
            }`}</div>
            {sellerBankAccountNumber && (
              <div style={{ minWidth: '40px' }}>
                <div style={{ fontWeight: 'bold' }}>{seller[0]?.profile?.contract?.bankName}</div>
                <div>{seller[0]?.profile?.contract?.bankAccountNumber}</div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <div>
                {seller[0].profile?.contract?.licensePhoto && (
                  <PhotoView src={cdnImageUrl(seller[0].profile?.contract?.licensePhoto)}>
                    <Button>신분증</Button>
                  </PhotoView>
                )}
              </div>
              <div>
                {seller[0].profile?.contract?.bankAccountPhoto && (
                  <PhotoView src={cdnImageUrl(seller[0].profile?.contract?.bankAccountPhoto)}>
                    <Button>통장</Button>
                  </PhotoView>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: '일정변경',
      render: (record: ProductOrderViewModel) => {
        if (!record) {
          return '-';
        }
        const saleType = record?.product?.saleType ? record?.product?.saleType : 'commerce';
        const purchases =
          record?.purchases?.length > 0 ? record?.purchases : record?.purchase ? [record?.purchase] : [];
        const combinedRefundItems = combinedRefundItemsByName(record?.refunds);
        const remainPurchases = getRemainPurchases(purchases, combinedRefundItems);

        return (
          <Button
            onClick={() => {
              changeScheduleModalStore.open({
                channel: record?.channel as SalesChannelsType,
                saleType: saleType,
                orderId: record?._id,
                productName: record?.product?.name || '',
                buyerName: record?.buyer.name || '',
                purchases: purchases,
                refunds: record?.refunds,
                remainPurchases: remainPurchases,
                paidAmount: record?.payment.amount,
                paidAt: (record?.payment?.paidAt as Date) || new Date(),
                productId: record?.product?.id || '',
                memo: record?.memo,
                classInvoiceId: record?.product?.classInvoiceId,
              });
            }}>
            일정변경
          </Button>
        );
      },
      align: 'center',
    },
    {
      title: '상품명',
      render: (record) => {
        if (!record) {
          return '-';
        }
        const productDetail = record?.productDetail?.[0];
        const product = productDetail || record?.product || '-';
        const marketLabel = getMarketSectionLabel(record?.product);
        return (
          <Popover content={product?.title || product?.name}>
            <div style={{ width: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {product?.title || product?.name}
              {marketLabel !== '-' ? (
                <Tag color="orange" style={{ marginLeft: 6 }}>
                  {marketLabel}
                </Tag>
              ) : null}
            </div>
          </Popover>
        );
      },
    },
    {
      title: '구매옵션',
      align: 'center',
      width: 250,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const purchase = record?.purchase;
        const purchases = record?.purchases;
        const scheduleHistory = record?.scheduleHistory || [];
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
            }}>
            <div>{`${(purchases || (purchase ? [purchase] : []))
              .map((purchase: any) => {
                let optionName = '';
                if (purchase.option && purchase.option.length > 0) {
                  optionName = `${purchase.option.join(' ')}`;
                }
                if (Array.isArray(purchase.name)) {
                  return `${purchase.name.join('/')}(${purchase.count})`;
                }

                optionName = purchase.name;

                // if (purchase.appOptionName) {
                //   optionName = purchase.name;
                // } else if (scheduleHistory.length > 0) {
                //   optionName = `${optionName}${purchase.name}`;
                // } else {
                //   optionName = `${optionName}${purchase.name}` + (purchase.oldName ? ` / ${purchase.oldName}` : '');
                // }
                const newOptionNames: string[] = [];
                optionName.split(' / ').forEach((name) => {
                  const newName = name.trim();
                  if (newName && newName !== 'undefined' && !newOptionNames.includes(newName)) {
                    newOptionNames.push(newName);
                  }
                });
                optionName = newOptionNames.join(' / ') || purchase.originName || '';
                return `${optionName}(${purchase.count})`;
              })
              .join('\n')}`}</div>
            <div>
              {scheduleHistory.length > 0 && (
                <Button
                  onClick={() => {
                    setSelectedScheduleHistory(scheduleHistory);
                  }}>
                  변경됨
                </Button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: '수업일',
      width: 250,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const plannedDates =
          record?.plannedDates?.length > 0 ? record?.plannedDates : record?.plannedDate ? [record?.plannedDate] : [];
        if (plannedDates?.length > 0) {
          return (
            <div style={{ fontWeight: 'bold' }}>
              {plannedDates
                .map((plannedDate: Date) => {
                  return `${moment(plannedDate).format('YYYY. MM. DD. HH:mm')}`;
                })
                .join('\n')}
            </div>
          );
        }

        // 수업일이 없는 경우 업데이트 버튼 표시
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ color: '#999' }}>-</div>
            <Button
              size="small"
              type="primary"
              loading={updateProductOrderSchedulesMutation.isPending}
              onClick={() => {
                updateProductOrderSchedulesMutation.mutate({
                  uid: record.uid || record.uuid,
                  channel: record.channel,
                });
              }}>
              수업일 생성
            </Button>
          </div>
        );
      },
    },
    {
      title: '구매요청/메모',
      align: 'left',
      width: 250,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const qnaMemo = record?.memo?.qna;
        const paymentMemo = record?.memo?.payment;
        const shippingMemo = record?.memo?.shipping;
        const memoList = [];
        if (qnaMemo) {
          memoList.push({ title: '입력옵션', answer: qnaMemo });
        }
        if (paymentMemo) {
          memoList.push({ title: '결제메모', answer: paymentMemo });
        }
        if (shippingMemo) {
          const title = record?.channel === SalesChannelsType.NAVER_STORE ? '요청사항' : '배송메모';
          memoList.push({ title: title, answer: shippingMemo });
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {memoList.length > 0
              ? memoList.map((memo) => {
                  return (
                    <div style={{ display: 'flex', gap: 0, whiteSpace: 'pre-wrap', flexDirection: 'column' }}>
                      <div style={{ fontWeight: 'bold' }}>{memo.title}</div>
                      <div>{memo.answer}</div>
                    </div>
                  );
                })
              : '-'}
            <Button
              style={{ alignSelf: 'center' }}
              onClick={() => {
                setEditItem({ data: record, type: 'memo' });
              }}>
              수정
            </Button>
          </div>
        );
      },
    },
    {
      title: '파트너 메모',
      align: 'left',
      width: 250,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const noteMemo = record?.memo?.note || '';
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}>
            {noteMemo && (
              <div style={{ maxWidth: '200px' }}>
                <LinesEllipsis text={noteMemo} maxLine={3} ellipsis="..." trimRight></LinesEllipsis>
              </div>
            )}
            <Button
              onClick={() => {
                dialog.open({
                  title: '파트너 메모',
                  content: (
                    <div style={{ border: '1px solid #D5D8DD', padding: '8px', height: '100px' }}>
                      <textarea
                        ref={memoRef}
                        style={{
                          color: '#424242',
                          border: 'none',
                          outline: 'none',
                          width: '100%',
                          height: '100%',
                          resize: 'none',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '22px',
                        }}
                        placeholder={'고객에게는 노출되지않는 메모란입니다.'}
                        defaultValue={noteMemo}
                      />
                    </div>
                  ),
                  type: 'ok',
                  onConfirm: () => {
                    const uid = record?.uuid || record?.uid;
                    if (uid && memoRef.current) {
                      updateProductOrderMemoMutation.mutate({
                        uid: record?.uuid || record?.uid || '',
                        memo: { note: memoRef.current?.value || '' },
                      });
                    }
                  },
                });
              }}>
              {noteMemo ? '수정' : '작성'}
            </Button>
          </div>
        );
      },
    },
    {
      title: '수강증(상태)',
      dataIndex: 'classInvoice',
      align: 'center',
      render: (classInvoice) => {
        if (!classInvoice || classInvoice.length === 0 || classInvoice[0]._id === null) {
          return '-';
        }
        const classInvoiceStatus = classInvoice && classInvoice[0]?.status;
        let status = '-';
        if (
          classInvoiceStatus === LegacyClassInvoiceStatus.CLASS_COMPLETED ||
          classInvoiceStatus === ClassInvoiceStatus.CLASS_COMPLETED
        ) {
          status = '수강 완료';
        } else if (
          classInvoiceStatus === LegacyClassInvoiceStatus.PAYMENT_COMPLETED ||
          classInvoiceStatus === ClassInvoiceStatus.PAYMENT_COMPLETED
        ) {
          status = '진행중';
        } else if (
          classInvoiceStatus === LegacyClassInvoiceStatus.WAITING_FOR_PAYMENT ||
          classInvoiceStatus === ClassInvoiceStatus.WAITING_FOR_PAYMENT
        ) {
          status = '결제 요청';
        } else if (
          classInvoiceStatus === LegacyClassInvoiceStatus.CREATED ||
          classInvoiceStatus === ClassInvoiceStatus.CREATED
        ) {
          status = '채팅 신정';
        } else {
          status = classInvoiceStatus;
        }
        return (
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>{status}</div>
            <Button
              onClick={() => {
                classInvoiceModalStore.open({
                  classId: classInvoice[0].programId,
                  classInvoiceId: classInvoice[0]._id,
                  mode: 'view',
                });
              }}>
              수강증 보기
            </Button>
          </div>
        );
      },
      width: 130,
    },
    {
      title: '금액',
      render: (record) => {
        if (!record) {
          return '-';
        }
        const payment = record?.payment;
        let refundAmount = record?.refund?.amount;
        if (
          !refundAmount &&
          [SalesChannelsType.NAVER_STORE, SalesChannelsType.EZWEL].includes(record?.channel) &&
          record?.statuses.refund === RefundStatus.ALL
        ) {
          refundAmount = record?.payment?.amount;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>총결제</div>
              <div>
                {payment?.amount?.toLocaleString()}원{payment?.point ? `(${payment?.point?.toLocaleString()}P)` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>실결제</div>
              <div>{payment?.billingAmount?.toLocaleString()}원</div>
            </div>
            {payment?.couponDiscountAmount !== undefined && payment?.couponDiscountAmount > 0 && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div
                  style={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: '#1890ff',
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    setSelectedUsedCoupons(payment?.usedCoupons || []);
                    setShowUsedCouponsModal(true);
                  }}>
                  쿠폰
                </div>
                <div
                  style={{
                    fontSize: '12px',
                  }}>{`${payment?.couponDiscountAmount.toLocaleString()}원 할인`}</div>
              </div>
            )}
            {payment?.membershipDiscountAmount !== undefined && payment?.membershipDiscountAmount > 0 && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ fontWeight: 'bold' }}>멤버십</div>
                <div
                  style={{
                    fontSize: '12px',
                  }}>{`${payment?.membershipDiscountAmount.toLocaleString()}원 멤버십 할인${
                  formatMembershipDiscountLabel(payment?.membershipDiscountDetail)
                    ? ` (${formatMembershipDiscountLabel(payment?.membershipDiscountDetail)})`
                    : ''
                }`}</div>
              </div>
            )}
            {payment?.bankAccountTransferAmount !== undefined && payment?.bankAccountTransferAmount > 0 && (
              <div style={{ display: 'flex', gap: '4px', color: '#424242' }}>
                <div style={{ fontWeight: 'bold' }}>계좌이체</div>
                <div>{`(계좌이체: ${payment?.bankAccountTransferAmount?.toLocaleString()}원)`}</div>
              </div>
            )}
            {refundAmount !== undefined && refundAmount > 0 && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ fontWeight: 'bold' }}>환불</div>
                <div style={{ color: 'red' }}>{`-${refundAmount.toLocaleString()}원`}</div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '결제 적립',
      width: 120,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const paybackPoint = record?.payback?.amount || 0;
        if (paybackPoint) return `${paybackPoint.toLocaleString()}P`;
        return '-';
      },
    },
    {
      title: '결제수단',
      width: 120,
      render: (record) => {
        if (!record) {
          return '-';
        }
        if (
          [SalesChannelsType.NAVER_STORE, SalesChannelsType.KAKAO_RESERVATION].includes(record?.channel) &&
          record?.payment?.method
        ) {
          return `${record?.payment?.method}`;
        }
        return getPaymentMethodText(record?.payment);
      },
    },
    {
      title: '시간',
      align: 'center',
      width: 220,
      render: (record) => {
        if (!record) {
          return '-';
        }
        const refundStatus = record?.statuses?.refund;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>생성</div>
              <div>{moment(record?.createdAt).format('YYYY.MM.DD HH:mm')}</div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>결제</div>
              <div>{record?.payment?.paidAt ? moment(record?.payment.paidAt).format('YYYY.MM.DD HH:mm') : '-'}</div>
            </div>
            {[RefundStatus.REQUESTED, RefundStatus.ACCEPTED, RefundStatus.DENIED].includes(refundStatus) && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ fontWeight: 'bold' }}>환불요청</div>
                <div>
                  {record?.refund?.requestedAt ? moment(record?.refund.requestedAt).format('YYYY.MM.DD HH:mm') : '-'}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>업데이트</div>
              <div>{moment(record?.updatedAt).format('YYYY.MM.DD HH:mm')}</div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div style={{ fontWeight: 'bold' }}>결제완성</div>
              <div>{record?.completionDate ? moment(record?.completionDate).format('YYYY.MM.DD HH:mm') : '-'}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: '결제상태',
      dataIndex: 'statuses',
      render: (statuses) => {
        const payStatus = (statuses?.payment || '').toLowerCase();
        let status = '-';
        if (payStatus === 'done') {
          status = '결제완료';
        } else if (payStatus === 'ready') {
          status = '결제대기';
        } else if (payStatus === 'error') {
          status = '발송실패';
        } else if (payStatus === 'cancel') {
          status = '결제취소';
        } else if (payStatus === 'none') {
          status = '-';
        } else {
          status = payStatus;
        }
        return status;
      },
    },
    {
      title: '배송상태',
      dataIndex: 'statuses',
      render: (statuses) => {
        const status = `${statuses?.shipping}`.toLowerCase();
        if (status === 'none' || !statuses?.shipping) {
          return '-';
        } else {
          return status;
        }
      },
      width: 84,
    },
    {
      title: '리포트 상태',
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }

        const saleType = data.product.saleType;
        const reportStatus = data.statuses?.report || '';
        let status = '-';
        if (saleType === 'class') {
          if (reportStatus === PaymentStatementReportStatus.PUBLISHED) {
            const publishedAt = moment(data.report?.publishedAt).format('YYYY-MM-DD') || '-';
            status = `발송완료\n(${publishedAt})`;
          } else if (reportStatus === PaymentStatementReportStatus.EDITTING) {
            const createdAt = moment(data.report?.createdAt).format('YYYY-MM-DD') || '-';
            status = `임시저장\n(${createdAt})`;
          } else {
            status = '미작성';
          }
        }
        return status;
      },
      width: 100,
    },
    {
      title: '외부채널\n환불기록',
      render: (data: ProductOrderViewModel) => {
        if (!data?.channel) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              setSelectedNaverOrder(data);
              setShowUpdateNaverRefundModal(true);
            }}>
            수정
          </Button>
        );
      },
      width: 100,
    },
    {
      title: '계좌이체\n금액 추가',
      width: 100,
      render: (data: ProductOrderViewModel) => {
        const appOrder = !data?.channel;
        if (!appOrder) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              addAccountTransferModalStore.open({
                _id: data._id,
                memo: data?.memo,
                payment: data?.payment,
                buyer: data?.buyer,
                product: data.product,
              });
            }}>
            추가
          </Button>
        );
      },
      align: 'center',
    },
    {
      title: '결제완성일\n설정',
      width: 100,
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              updateCompletionDateModalStore.open({
                uid: data?.uid || data?.uuid,
                memo: data?.memo?.settlement,
                completionDate: data?.completionDate,
              });
            }}>
            설정
          </Button>
        );
      },
      align: 'center',
    },
    {
      title: '상세',
      width: 100,
      render: (data: ProductOrderViewModel) => {
        if (!data) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              setDetailOrder(data);
            }}>
            상세보기
          </Button>
        );
      },
      align: 'center',
    },
  ];

  useEffect(() => {
    const searchResult = productOrderSearchQuery.data?.data;
    if (searchResult) {
      store.setProductOrdersContainer(searchResult);
    }
    setSelectedItemIndex([]);
  }, [productOrderSearchQuery.data?.data]);

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    store.setStartDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    store.setEndDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const requestRefundProductOrdersMutation = useMutation({
    mutationFn: productOrderApi.requestRefundProductOrders,
    onSuccess: async (response) => {
      const result: {
        app: RefundProductOrdersResponse;
        naver: RefundProductOrdersResponse;
        kakao: RefundProductOrdersResponse;
        benepia: RefundProductOrdersResponse;
      } = response.data;
      let showCopyButton = false;
      let appContent =
        result?.app?.targetIds?.length > 0
          ? `앱 ${result?.app?.targetIds?.length}개 대상\n${result?.app?.success} 성공\n${result?.app?.failed} 실패`
          : '';
      if (result?.app?.failed > 0) {
        appContent += `\n실패주문번호\n${result?.app?.failedIds?.join('\n')}`;
      }
      let naverContent =
        result?.naver?.targetIds?.length > 0
          ? `네이버 ${result?.naver?.targetIds?.length}개 대상\n${result?.naver?.success} 성공\n${result?.naver?.failed} 실패`
          : '';
      if (result?.naver?.needToNaverAdminCancelOrderIds && result?.naver?.needToNaverAdminCancelOrderIds?.length > 0) {
        showCopyButton = true;
        naverContent += `\n네이버 직권취소 필요 주문번호 목록\n${result?.naver.needToNaverAdminCancelOrderIds.join(
          '\n',
        )}`;
      }
      if (result?.naver?.failed > 0) {
        naverContent += `\n실패주문번호\n${result?.naver?.failedIds?.join('\n')}`;
      }
      let kakaoContent =
        result?.kakao?.targetIds?.length > 0
          ? `카카오 ${result?.kakao?.targetIds?.length}개 대상\n${result?.kakao?.success} 성공\n${result?.kakao?.failed} 실패`
          : '';
      if (result?.kakao?.failed > 0) {
        kakaoContent += `\n실패주문번호\n${result?.kakao?.failedIds?.join('\n')}`;
      }
      let benepiaContent =
        result?.benepia?.targetIds?.length > 0
          ? `베네피아 ${result?.benepia?.targetIds?.length}개 대상\n${result?.benepia?.success} 성공\n${result?.benepia?.failed} 실패`
          : '';
      if (result?.benepia?.failed > 0) {
        benepiaContent += `\n실패주문번호\n${result?.benepia?.failedIds?.join('\n')}`;
      }
      const content = (
        <div>
          <div>{appContent}</div>
          <div>{naverContent}</div>
          <div>{kakaoContent}</div>
          <div>{benepiaContent}</div>
          {showCopyButton && (
            <Button
              style={{ marginLeft: '5px' }}
              onClick={() => {
                const orderIdsText = (result?.naver.needToNaverAdminCancelOrderIds || []).join('\n');
                navigator.clipboard.writeText(orderIdsText);
                toast.dark(`주문번호를 복사했습니다`, {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              직권취소 필요 주문번호 목록 복사
            </Button>
          )}
        </div>
      );
      dialog.open({
        title: '환불 처리 결과',
        content,
        type: 'ok',
      });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const updateProductOrderMemoMutation = useMutation({
    mutationFn: productOrderApi.updateProductOrderMemo,
    onSuccess: async () => {
      productOrderSearchQuery.refetch();
      toast.dark('업데이트 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: `${e}`,
      });
    },
  });

  const updateProductOrderBuyerInfoMutation = useMutation({
    mutationFn: productOrderApi.updateProductOrderBuyerInfo,
    onSuccess: async () => {
      productOrderSearchQuery.refetch();
      toast.dark('업데이트 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: `${e}`,
      });
    },
  });

  const excludeSettlementTargetMutation = useMutation({
    mutationFn: productOrderApi.excludeSettlementTarget,
    onSuccess: async () => {
      productOrderSearchQuery.refetch();
      toast.dark('반영 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: `${e?.response?.data?.message}`,
      });
    },
  });

  const bulkRefundExecute = () => {
    const { page = 0, size = 0 } = store.searchQuery;
    const maxSize = Math.min((page + 1) * size, store.searchResultContainer.contents.length);
    const currentPageResultList = store.searchResultContainer.contents.slice(page * size, maxSize) || [];
    const paymentStatementIds: string[] = [];
    const naverOrderUids: string[] = [];
    const kakaoOrderUids: string[] = [];
    const benepiaOrderUids: string[] = [];
    let ezwelOrderCount = 0;
    selectedItemIndex.forEach((index) => {
      const productOrder = currentPageResultList[index];
      if (productOrder) {
        const channel = productOrder?.channel;
        if (channel === SalesChannelsType.NAVER_STORE) {
          naverOrderUids.push(productOrder.uid);
        } else if (channel === SalesChannelsType.KAKAO_RESERVATION) {
          kakaoOrderUids.push(productOrder.uid);
        } else if (channel === SalesChannelsType.BENEPIA) {
          benepiaOrderUids.push(productOrder.uid);
        } else if (channel === SalesChannelsType.EZWEL) {
          ezwelOrderCount += 1;
        } else {
          paymentStatementIds.push(productOrder._id);
        }
      }
    });

    const refundableCount =
      paymentStatementIds.length + naverOrderUids.length + kakaoOrderUids.length + benepiaOrderUids.length;
    if (refundableCount < 1) {
      dialog.open({
        title: '이지웰 환불 안내',
        content: (
          <div>
            이지웰 결제건은 어드민에서 환불할 수 없습니다.
            <br />
            상품주문번호와 상세내용을 이지웰 소통 담당자에게 전달해주세요.
          </div>
        ),
        type: 'ok',
      });
      return;
    }

    dialog.open({
      title: '환불',
      content: `총 ${refundableCount}건을 일괄환불 하시겠습니까?\n-앱: ${paymentStatementIds.length}건\n-네이버: ${
        naverOrderUids.length
      }건\n-카카오: ${kakaoOrderUids.length}건\n-베네피아: ${benepiaOrderUids.length}건${
        ezwelOrderCount > 0 ? `\n-이지웰: ${ezwelOrderCount}건 (어드민 환불 불가, 제외)` : ''
      }`,
      type: 'ok_cancel',
      onConfirm: async () => {
        const requestBody = {
          paymentStatementIds,
          naverOrderUids,
          kakaoOrderUids,
          benepiaOrderUids,
        };
        requestRefundProductOrdersMutation.mutate(requestBody);
      },
    });
  };

  const bulkRefundReserve = () => {
    dialog.open({
      title: '환불 예약',
      content: `${selectedItemIndex.length}건을 환불예약 하시겠습니까?`,
      type: 'ok_cancel',
      onConfirm: async () => {
        const { page = 0, size = 0 } = store.searchQuery;
        const maxSize = Math.min((page + 1) * size, store.searchResultContainer.contents.length);
        const currentPageResultList = store.searchResultContainer.contents.slice(page * size, maxSize) || [];
        const paymentStatementIds: string[] = [];
        selectedItemIndex.forEach((index) => {
          const paymentStatement = currentPageResultList[index];
          if (paymentStatement && paymentStatement.channel !== SalesChannelsType.EZWEL) {
            paymentStatementIds.push(paymentStatement._id);
          }
        });
        payment.registerRefunds(paymentStatementIds);
      },
    });
  };

  const getExcelFile = async () => {
    try {
      setIsDownloadingBasic(true);
      const fileName = `igogo-product-orders-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const blob = await productOrderApi.getSearchProductOrdersExcelFile(store.searchQuery);
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error('Excel download failed:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setIsDownloadingBasic(false);
    }
  };

  const getExcelFilePB = async () => {
    try {
      setIsDownloadingPB(true);
      const startDate = store.searchQuery.startDate;
      const endDate = store.searchQuery.endDate;
      const fileName = `챌린저스-${moment(startDate).format('YYYYMMDD-HHmmss')}~${moment(endDate).format(
        'YYYYMMDD-HHmmss',
      )}.xlsx`;
      const blob = await productOrderApi.getPBProductOrdersExcelFile(store.searchQuery);
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error('Excel download failed:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setIsDownloadingPB(false);
    }
  };

  const getExcelFileCommercePlanned = async () => {
    try {
      setIsDownloadingCommerce(true);
      const startDate = store.searchQuery.startDate;
      const endDate = store.searchQuery.endDate;
      const fileName = `커머스-${moment(startDate).format('YYYYMMDD-HHmmss')}~${moment(endDate).format(
        'YYYYMMDD-HHmmss',
      )}.xlsx`;
      const blob = await productOrderApi.getPlannedCommerceProductOrdersExcelFile(store.searchQuery);
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error('Excel download failed:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setIsDownloadingCommerce(false);
    }
  };

  const getMarketingAgreedUsersExcelFile = async () => {
    try {
      setIsDownloadingMarketing(true);
      const startDate = store.searchQuery.startDate;
      const endDate = store.searchQuery.endDate;
      const fileName = `마케팅동의유저-${moment(startDate).format('YYYYMMDD-HHmmss')}~${moment(endDate).format(
        'YYYYMMDD-HHmmss',
      )}.xlsx`;
      const blob = await productOrderApi.getMarketingAgreedUsersExcelFile(store.searchQuery);
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error('Excel download failed:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setIsDownloadingMarketing(false);
    }
  };

  const checkCanBulkRefundCondition = () => {
    const { page = 0, size = 0 } = store.searchQuery;
    const maxSize = Math.min((page + 1) * size, store.searchResultContainer.contents.length);
    const currentPageResultList = store.searchResultContainer.contents.slice(page * size, maxSize) || [];
    const canBulkRefund = (selectedItemIndex || []).every((index) => {
      const productOrder = currentPageResultList[index];
      const refundStatus = productOrder?.statuses?.refund;

      if ([RefundStatus.ACCEPTED, RefundStatus.RESERVED].includes(refundStatus)) {
        return true;
      }
      return false;
    });
    return canBulkRefund;
  };

  const checkCanBulkRefundRegisterCondition = () => {
    const { page = 0, size = 0 } = store.searchQuery;
    const maxSize = Math.min((page + 1) * size, store.searchResultContainer.contents.length);
    const currentPageResultList = store.searchResultContainer.contents.slice(page * size, maxSize) || [];
    const canBulkRefundRegister = (selectedItemIndex || []).every((index) => {
      const paymentStatement = currentPageResultList[index];
      if (paymentStatement?.statuses.refund === RefundStatus.NONE) {
        return true;
      }
      return false;
    });
    return canBulkRefundRegister;
  };

  return (
    <div className="dashboard">
      {updateCompletionDateModalStore.show && <UpdateCompletionDateModal />}
      {!!editItem && (
        <Modal
          width={600}
          open={!!editItem}
          onCancel={() => {
            setEditItem(undefined);
            setEditBuyerInfo(undefined);
            setEditMemo(undefined);
          }}
          footer={
            <div>
              <Button
                onClick={() => {
                  setEditItem(undefined);
                  setEditBuyerInfo(undefined);
                  setEditMemo(undefined);
                }}>
                취소
              </Button>
              <Button
                style={{ color: 'red' }}
                onClick={() => {
                  if (editItem && editMemo) {
                    const data = {
                      uid: editItem?.data?.uuid || editItem?.data?.uid,
                      memo: editMemo,
                    };
                    updateProductOrderMemoMutation.mutate(data);
                    setEditItem(undefined);
                    setEditBuyerInfo(undefined);
                    setEditMemo(undefined);
                  }

                  if (editItem && editBuyerInfo) {
                    const data = {
                      uid: editItem?.data?.uuid || editItem?.data?.uid,
                      buyerInfo: editBuyerInfo,
                    };
                    updateProductOrderBuyerInfoMutation.mutate(data);
                    setEditItem(undefined);
                    setEditBuyerInfo(undefined);
                    setEditMemo(undefined);
                  }
                }}>
                저장하기
              </Button>
            </div>
          }>
          {editItem?.type === 'memo' && (
            <EditMemo
              data={editItem?.data}
              onChange={(memo: ProductOrderMemo) => {
                setEditMemo(memo);
              }}
            />
          )}
          {editItem?.type === 'buyerInfo' && (
            <EditBuyerInfo
              data={editItem?.data}
              onChange={(buyerInfo: {
                buyer: ProductOrderViewModel['buyer'];
                receiver?: ProductOrderViewModel['receiver'];
              }) => {
                setEditBuyerInfo(buyerInfo);
              }}
            />
          )}
        </Modal>
      )}
      {showCreateOrder && (
        <Modal
          width={'480px'}
          open={showCreateOrder}
          footer={null}
          onCancel={() => {
            setShowCreateOrder(false);
          }}
          onOk={() => {
            setShowCreateOrder(false);
          }}>
          <CreateBankAccountTransferOrder
            onClose={() => {
              setShowCreateOrder(false);
            }}
            onCreate={() => {
              setShowCreateOrder(false);
              productOrderSearchQuery.refetch();
            }}
          />
        </Modal>
      )}
      <div className="page-header">
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div>통합 결제내역</div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <div>
            <Button
              style={{ width: '160px', marginRight: '50px' }}
              onClick={() => {
                setShowCreateOrder(true);
              }}>
              계좌이체 결제내역 생성
            </Button>
          </div>
          <div>
            <Button
              style={{ borderColor: store.searchQuery.dateType === 'payment.paidAt' ? 'blue' : '#d9d9d9' }}
              onClick={() => {
                store.updateSearchQuery({ dateType: 'payment.paidAt' });
              }}>
              결제일
            </Button>
          </div>
          <div>
            <Button
              style={{ borderColor: store.searchQuery.dateType === 'createdAt' ? 'blue' : '#d9d9d9' }}
              onClick={() => {
                store.updateSearchQuery({ dateType: 'createdAt' });
              }}>
              생성일
            </Button>
          </div>
          <div>
            <Button
              style={{ borderColor: store.searchQuery.dateType === 'updatedAt' ? 'blue' : '#d9d9d9' }}
              onClick={() => {
                store.updateSearchQuery({ dateType: 'updatedAt' });
              }}>
              수정일
            </Button>
          </div>
          <div>
            <Button
              style={{ borderColor: store.searchQuery.dateType === 'plannedDates' ? 'blue' : '#d9d9d9' }}
              onClick={() => {
                store.updateSearchQuery({ dateType: 'plannedDates' });
              }}>
              수업일
            </Button>
          </div>
          <div>
            <Button
              style={{ borderColor: store.searchQuery.dateType === 'refund.refundedAt' ? 'blue' : '#d9d9d9' }}
              onClick={() => {
                store.updateSearchQuery({ dateType: 'refund.refundedAt' });
              }}>
              환불일
            </Button>
          </div>
          <div style={{ marginLeft: '10px' }}>
            <DatePicker
              onChange={onChangeStartDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.startDate as any))}
            />
          </div>
          <div style={{ marginLeft: '8px' }}>
            <DatePicker
              onChange={onChangeEndDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.endDate as any))}
            />
          </div>
        </div>
      </div>
      <div className="page-content">
        <div style={{ display: 'flex', marginBottom: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexWrap: 'wrap' }}>
            <div
              style={{
                lineHeight: '2rem',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}>
              <FilterSelect
                inactiveValue="all"
                value={store.searchQuery.channel || 'all'}
                style={{ width: '120px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ channel: value });
                }}
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'app', label: '앱' },
                  { value: 'naver', label: '네이버' },
                  { value: 'kakao', label: '카카오' },
                  { value: 'benepia', label: '베네피아' },
                  { value: 'ezwel', label: '이지웰' },
                  { value: 'inbound', label: '인바운드' },
                ]}
              />
              <FilterSelect
                inactiveValue="all"
                value={store.searchQuery.saleType || 'all'}
                style={{ width: '120px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ saleType: value });
                }}
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'commerce', label: '커머스' },
                  { value: 'class', label: '클래스' },
                ]}
              />
              <FilterSelect
                inactiveValue="all"
                value={store.searchQuery.marketSection || 'all'}
                style={{ width: '130px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ marketSection: value });
                }}
                options={MARKET_SECTION_FILTER_OPTIONS}
              />
              <FilterSelect
                inactiveValue=""
                value={store.searchQuery?.refundStatus || ''}
                style={{ width: '120px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ refundStatus: value });
                }}
                options={[
                  { value: '', label: '전체' },
                  { value: RefundStatus.ALL, label: '전체환불' },
                  { value: RefundStatus.PARTIAL, label: '부분환불' },
                  { value: RefundStatus.ACCEPTED, label: '환불예정' },
                  { value: RefundStatus.DENIED, label: '환불반려' },
                  { value: RefundStatus.REQUESTED, label: '환불요청' },
                  { value: RefundStatus.RESERVED, label: '환불예약' },
                  { value: RefundStatus.REQUEST_CANCELLED, label: '환불철회' },
                  { value: RefundStatus.NONE, label: '결제완료' },
                ]}
              />
              <FilterSelect
                inactiveValue=""
                value={store.searchQuery?.settlementStatus || ''}
                style={{ width: '120px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ settlementStatus: value });
                }}
                options={[
                  { value: '', label: '전체' },
                  { value: PaymentStatementSettlementStatus.DONE, label: '정산완료' },
                  { value: PaymentStatementSettlementStatus.NONE, label: '미정산' },
                ]}
              />
              <FilterSelect
                inactiveValue=""
                value={store.searchQuery?.completionStatus || ''}
                style={{ width: '120px' }}
                onChange={(value) => {
                  store.updateSearchQuery({ completionStatus: value });
                }}
                options={[
                  { value: '', label: '전체' },
                  { value: 'payment_completion', label: '결제완성' },
                  { value: 'payment_not_completion', label: '결제완성X' },
                ]}
              />
              <FilterSelect
                inactiveValue="all"
                style={{ width: '240px' }}
                value={store.searchQuery.type || 'all'}
                onChange={(value) => {
                  store.updateSearchQuery({ type: value });
                }}
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'no_planned_dates_settlement_target', label: '비일정옵션 정산대상(전체환불 제외)' },
                  { value: 'purchase_payback_done', label: '결제 적립된 내역' },
                  { value: 'exclude_refund_all', label: '전체환불 제외' },
                  { value: 'excluded_settlement_target', label: '정산대상 제외 내역' },
                ]}
              />
              <Input
                className={filterActiveClassName(!!store.searchQuery.keyword)}
                ref={searchInputRef}
                style={{ width: '240px' }}
                value={searchKeyword}
                placeholder={'검색어 입력 후 엔터'}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.code === 'Enter' && searchKeyword) {
                    store.updateSearchQuery({ keyword: searchKeyword });
                  }
                }}
              />
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
              <div style={{ fontWeight: 'bold' }}>검색결과: {store.searchResultContainer.total}</div>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '36px', gap: '10px' }}>
              <Popover content={<div>(앱 결제)커머스 일괄 환불예약은 "결제완료" 건만 가능합니다.</div>}>
                <Button
                  disabled={
                    !selectedItemIndex.length ||
                    !checkCanBulkRefundRegisterCondition() ||
                    !!(store?.searchQuery?.channel !== 'app')
                  }
                  onClick={bulkRefundReserve}>
                  환불 예약
                </Button>
              </Popover>
              <Popover content={<div>커머스 일괄환불은 "예약", "예정" 된 건에 대해서만 환불처리합니다.</div>}>
                <Button
                  disabled={
                    selectedItemIndex.length === 0 ||
                    store.searchQuery.saleType === 'class' ||
                    !checkCanBulkRefundCondition()
                  }
                  onClick={bulkRefundExecute}>
                  일괄 환불
                </Button>
              </Popover>
              {store.searchQuery?.refundStatus === RefundStatus.ACCEPTED && (
                <Checkbox
                  onChange={(e) => {
                    store.updateSearchQuery({ isPartnerRefundRequested: e.target.checked });
                  }}
                  checked={store.searchQuery.isPartnerRefundRequested}>
                  파트너 직접 환불요청(예정)건만 보기
                </Checkbox>
              )}
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ marginTop: '6px', marginRight: '10px' }}>검색결과 엑셀 파일 다운로드</div>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                <Button
                  loading={isDownloadingBasic}
                  onClick={() => {
                    getExcelFile();
                  }}>
                  기본
                </Button>
                <Button
                  loading={isDownloadingPB}
                  onClick={() => {
                    getExcelFilePB();
                  }}>
                  챌린저스
                </Button>
                <Button
                  loading={isDownloadingCommerce}
                  onClick={() => {
                    getExcelFileCommercePlanned();
                  }}>
                  판매자별
                </Button>
                <Button
                  loading={isDownloadingMarketing}
                  onClick={() => {
                    getMarketingAgreedUsersExcelFile();
                  }}>
                  마케팅 동의 유저목록
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Table
          columns={TABLE_COLUMNS}
          scroll={{ x: 'max-content', y: 'calc(100dvh - 330px)' }}
          style={{ whiteSpace: 'pre-wrap' }}
          rowKey={(record, index) => record?._id || `${index}`}
          dataSource={store.searchResultContainer.contents}
          pagination={{
            current: (store.searchResultContainer.page || 0) + 1,
            pageSize: store.searchResultContainer.size || 20,
            total: store.searchResultContainer.total,
            showSizeChanger: true,
          }}
          loading={productOrderSearchQuery.isLoading}
          onChange={(e) => {
            store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
          }}
        />
        <RefundModeModal />
        <ChangeScheduleModal />
        <Modal
          width={'480px'}
          centered
          open={selectedScheduleHistory.length > 0}
          style={{ zIndex: 9999 }}
          closable={false}
          onOk={() => {
            setSelectedScheduleHistory([]);
          }}
          onCancel={() => {
            setSelectedScheduleHistory([]);
          }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>구매옵션 변경 이력</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedScheduleHistory.map((history, index) => {
                return (
                  <div key={`schedule-history-${index}`} style={{ border: '1px solid #eaeaea', padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {history.beforeItems.map((item) => `${item.name}`).join('\n')}
                      </div>
                      <div>→</div>
                      <div style={{ whiteSpace: 'pre-wrap', fontWeight: 'bold' }}>
                        {history.afterItems
                          .map((item) => (item.name && item.count ? `${item.name}(${item.count})` : item))
                          .join('\n')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <div style={{ width: '80px' }}>수정</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {`${history.modifier.name}(${history.modifier.nickname}) - ${history.modifier.id}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <div style={{ width: '80px' }}>변경일시</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {moment(history.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      </div>
      {ezwelPartialRefundOrder && (
        <EzwelPartialRefundModal
          data={ezwelPartialRefundOrder}
          onClose={() => {
            setEzwelPartialRefundOrder(null);
          }}
          onUpdate={() => {
            setEzwelPartialRefundOrder(null);
            productOrderSearchQuery.refetch();
          }}
        />
      )}
      {showUpdateNaverRefundModal && selectedNaverOrder && (
        <UpdateNaverRefundModal
          data={selectedNaverOrder}
          onClose={() => {
            setShowUpdateNaverRefundModal(false);
            setSelectedNaverOrder(null);
          }}
          onUpdate={() => {
            setShowUpdateNaverRefundModal(false);
            setSelectedNaverOrder(null);
            productOrderSearchQuery.refetch();
          }}
        />
      )}
      <UsedCouponsModal
        visible={showUsedCouponsModal}
        onClose={() => setShowUsedCouponsModal(false)}
        usedCoupons={selectedUsedCoupons}
      />
      <ProductOrderDetailModal
        open={!!detailOrder}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onUpdated={async () => {
          setDetailOrder((prev) => {
            if (!prev || prev.channel !== SalesChannelsType.EZWEL) {
              return prev;
            }
            const now = moment();
            return {
              ...prev,
              origin: {
                ...(prev.origin || {}),
                useYn: 'Y',
                useCmplDt: now.format('YYYYMMDD'),
                useDtm: now.format('YYYYMMDDHHmmss'),
              },
            };
          });
          await productOrderSearchQuery.refetch();
        }}
      />
    </div>
  );
};

export default ProductOrdersPage;
